import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DIRECTUS_INSTANCE } from '../../api';
import reisloLogo from '../../img/logo-prose-captain.svg';
import backgroundImage from '../../img/bg-login-app.jpg';
import { Snackbar } from '../../ui/atoms';
import PasswordStrengthBar from 'react-password-strength-bar';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('error');
  const [tokenValid, setTokenValid] = useState(true);
  const turnstileContainerRef = useRef(null);

  const token = searchParams.get('token');

  useEffect(() => {
    // Check if token exists
    if (!token) {
      setTokenValid(false);
      setSnackbarMessage('Invalid or missing reset token. Please request a new password reset.');
      setSnackbarType('error');
      setShowSnackbar(true);
      return;
    }

    // Skip Turnstile on localhost
    if (window.location.hostname === 'localhost') {
      setTurnstileToken('dummy-token-for-localhost');
      return;
    }

    // Clear any existing Turnstile instances
    if (turnstileContainerRef.current) {
      turnstileContainerRef.current.innerHTML = '';
    }

    // Initialize Turnstile
    if (window.turnstile) {
      window.turnstile.render(turnstileContainerRef.current, {
        sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
        callback: function(token) {
          setTurnstileToken(token);
        },
        'theme': 'light',
        'refresh-expired': 'auto'
      });
    }

    // Copy ref to variable for cleanup
    const container = turnstileContainerRef.current;
    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setShowSnackbar(false);

    try {
      if (!turnstileToken) {
        throw new Error('Please complete the security check');
      }

      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (password.length < 8) {
        throw new Error('Password must be at least 8 characters long');
      }

      // Call to Directus reset password endpoint (to be created in flows)
      const response = await axios.post(`${DIRECTUS_INSTANCE}/auth/password/reset`, {
        token: token,
        password: password
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status >= 200 && response.status < 300) {
        setIsCompleted(true);
        setSnackbarMessage('Password has been successfully reset.');
        setSnackbarType('success');
        setShowSnackbar(true);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.errors?.[0]?.message || 
                          error.message || 
                          'Unable to reset password. Please try again or request a new reset link.';
      
      setTimeout(() => {
        setSnackbarMessage(errorMessage);
        setSnackbarType('error');
        setShowSnackbar(true);
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const SuccessScreen = () => (
    <div className="text-center">
      <div className="mb-8">
        <svg className="mx-auto h-16 w-16 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 className="text-2xl font-medium text-gray-900 mb-4">Password Reset Successful</h2>
      <p className="text-base text-gray-600 mb-8">
        Your password has been successfully updated. You can now sign in with your new password.
      </p>
      <button
        onClick={() => navigate('/signin')}
        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-500"
      >
        Continue to Sign In
      </button>
    </div>
  );

  const InvalidTokenScreen = () => (
    <div className="text-center">
      <div className="mb-8">
        <svg className="mx-auto h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h2 className="text-2xl font-medium text-gray-900 mb-4">Invalid Reset Link</h2>
      <p className="text-base text-gray-600 mb-8">
        This password reset link is invalid or has expired. Please request a new password reset.
      </p>
      <button
        onClick={() => navigate('/forgot-password')}
        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-500"
      >
        Request New Reset Link
      </button>
    </div>
  );

  if (!tokenValid) {
    return (
      <div className="relative min-h-screen h-screen flex items-center justify-center px-4 py-12 sm:px-6">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            alt=""
            src={backgroundImage}
            className="h-full w-full object-cover"
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
          />
        </div>

        {/* Centered Form Container */}
        <div className="relative z-10 w-full max-w-md">
          <div className="bg-white rounded-lg shadow-xl px-8 py-10">
            <InvalidTokenScreen />
          </div>
        </div>

        {showSnackbar && (
          <Snackbar
            message={snackbarMessage}
            type={snackbarType}
            onClose={() => setShowSnackbar(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="relative min-h-screen h-screen flex items-center justify-center px-4 py-12 sm:px-6">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          alt=""
          src={backgroundImage}
          className="h-full w-full object-cover"
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
        />
      </div>

      {/* Centered Form Container */}
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white rounded-lg shadow-xl px-8 py-6">
          {isCompleted ? (
            <SuccessScreen />
          ) : (
            <>
              <div>
                <img
                  className="h-10 w-auto hidden"
                  src={reisloLogo}
                  alt="Prose Captain logo"
                />
                <h2 className="mt-8 text-2xl font-medium text-gray-900">
                  Reset your password
                </h2>
                <p className="mt-2 text-base text-gray-500">
                  Enter your new password below.
                </p>
              </div>

              <div className="mt-10">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="password" className="block text-sm/6 font-medium text-gray-900">
                      New Password
                    </label>
                    <div className="mt-2">
                      <input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength="8"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="form-input-inset"
                        placeholder="Enter your new password"
                      />
                    </div>
                    <PasswordStrengthBar 
                      password={password}
                      minLength={8}
                      scoreWords={['Weak', 'Weak', 'Okay', 'Good', 'Strong']}
                      shortScoreWord="Too Short"
                    />
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm/6 font-medium text-gray-900">
                      Confirm New Password
                    </label>
                    <div className="mt-2">
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength="8"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="form-input-inset"
                        placeholder="Confirm your new password"
                      />
                    </div>
                    {confirmPassword && password !== confirmPassword && (
                      <p className="mt-1 text-sm text-red-600">Passwords do not match</p>
                    )}
                  </div>

                  {/* Turnstile container */}
                  <div ref={turnstileContainerRef} className="flex justify-center"></div>

                  <div>
                    <button
                      type="submit"
                      disabled={loading || !turnstileToken || !password || !confirmPassword || password !== confirmPassword}
                      className={`flex w-full justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm ${
                        !turnstileToken || !password || !confirmPassword || password !== confirmPassword || loading
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-primary-600 hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
                      }`}
                    >
                      {loading ? (
                        <>
                          <svg aria-hidden="true" className="w-4 h-4 mr-2 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                          </svg>
                          Resetting Password...
                        </>
                      ) : (
                        !turnstileToken ? 'Complete Security Check' : 'Reset Password'
                      )}
                    </button>
                  </div>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-500">
                    Remember your password?{' '}
                    <button
                      onClick={() => navigate('/signin')}
                      className="font-semibold text-primary-600 hover:text-primary-500"
                    >
                      Back to Sign In
                    </button>
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showSnackbar && (
        <Snackbar
          message={snackbarMessage}
          type={snackbarType}
          onClose={() => setShowSnackbar(false)}
        />
      )}
    </div>
  );
};

export default ResetPassword; 